/**
 * Art Directive — GPU-accelerated decorative overlays.
 * Lazy-initialised, IntersectionObserver-paused, RAF-driven, particle-pooled.
 */

import type { ElementPart } from 'lit'
import { noChange } from 'lit'
import { AsyncDirective, directive } from 'lit/async-directive.js'

import type { ArtOptions, ArtState } from './types'
import { syncOverlayRadius } from './utils'
import { createFacadeOverlay, animateFacade } from './effects/facade'
import { createSamwaOverlay, animateSamwa } from './effects/samwa'
import { createHowlOverlay, animateHowl } from './effects/howl'
import { createAttentionOverlay, animateAttention } from './effects/attention'
import { createSnowOverlay, animateSnow } from './effects/snow'
import { createStarfieldOverlay, animateStarfield } from './effects/starfield'
import { createGiftOverlay, animateGift } from './effects/gift'

interface ExtendedArtState extends ArtState {
	initialized: boolean
	originalPosition: string
	originalIsolation: string
}

class ArtDirective extends AsyncDirective {
	private state: ExtendedArtState | null = null

	render(options: ArtOptions) {
		void options
		return noChange
	}

	override update(part: ElementPart, [options]: [ArtOptions]) {
		const element = part.element as HTMLElement
		const {
			name,
			color,
			active = true,
			intensity = 1,
			speed = 1,
			density = 1,
			cometInterval = 30,
			cometSpeed = 12,
		} = options

		/**
		 * An element part cannot withhold the element — it is authored in the
		 * template either way — so `active: false` is the teardown, not a pause:
		 * no overlay node, no RAF, no observer, and the host's own `position` and
		 * `isolation` back as they were. `cleanup()` is a no-op before the first
		 * activation, so a host that starts inactive builds nothing at all.
		 */
		if (!active) {
			this.cleanup()
			return noChange
		}

		if (
			this.state &&
			(this.state.effect !== name ||
				this.state.color !== color ||
				this.state.intensity !== intensity ||
				this.state.speed !== speed ||
				this.state.density !== density ||
				this.state.cometInterval !== cometInterval ||
				this.state.cometSpeed !== cometSpeed)
		) {
			this.cleanup()
		}

		if (!this.state) {
			const originalPosition = element.style.position
			const originalIsolation = element.style.isolation

			const state: ExtendedArtState = {
				effect: name,
				color,
				intensity,
				speed,
				density,
				cometInterval,
				cometSpeed,
				element,
				isVisible: true,
				initialized: false,
				originalPosition,
				originalIsolation,
			}
			this.state = state

			requestAnimationFrame(() => {
				/**
				 * A toggle or an option change within the same frame tears this state
				 * down and builds a fresh one; the frame this callback was scheduled
				 * for still arrives, and running it against the successor would set up
				 * a second IntersectionObserver over the first — which nothing then
				 * holds a handle to disconnect.
				 */
				if (this.state !== state) return

				const rect = element.getBoundingClientRect()
				const isVisible =
					rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0

				if (!isVisible) {
					state.isVisible = false
					this.setupVisibilityObserver()
					return
				}

				this.initializeResources()
				this.startAnimation()
				this.setupVisibilityObserver()
			})
		}

		return noChange
	}

	private initializeResources(): void {
		if (!this.state || this.state.initialized) return

		const { element, effect: name } = this.state

		const computedStyle = getComputedStyle(element)
		if (computedStyle.position === 'static') {
			element.style.position = 'relative'
		}

		switch (name) {
			case 'facade':
				createFacadeOverlay(this.state)
				break
			case 'samwa':
				createSamwaOverlay(this.state)
				break
			case 'howl':
				createHowlOverlay(this.state)
				break
			case 'attention':
				createAttentionOverlay(this.state)
				break
			case 'snow':
				createSnowOverlay(this.state)
				break
			case 'starfield':
				createStarfieldOverlay(this.state)
				break
			case 'gift':
				createGiftOverlay(this.state)
				break
		}

		this.state.initialized = true
	}

	private setupVisibilityObserver(): void {
		if (!this.state || typeof IntersectionObserver === 'undefined') return

		this.state.observer = new IntersectionObserver(
			entries => {
				if (!this.state) return
				const isVisible = entries[0].isIntersecting

				if (isVisible && !this.state.isVisible) {
					this.state.isVisible = true

					if (!this.state.initialized) {
						this.initializeResources()
					}

					this.startAnimation()
				} else if (!isVisible && this.state.isVisible) {
					this.state.isVisible = false
					if (this.state.animationId) {
						cancelAnimationFrame(this.state.animationId)
						this.state.animationId = undefined
					}
				}
			},
			{ threshold: 0 },
		)

		this.state.observer.observe(this.state.element)
	}

	private startAnimation(): void {
		if (!this.state || !this.state.isVisible) return
		if (this.state.animationId) return

		if (!this.state.initialized) {
			this.initializeResources()
		}

		const animate = (currentTime: number) => {
			if (!this.state || !this.state.isVisible) {
				if (this.state) this.state.animationId = undefined
				return
			}

			/**
			 * A host's radius can change with the theme or a breakpoint, so the clip is
			 * re-read rather than fixed at mount — at 1 Hz, because getComputedStyle
			 * every frame is a layout read no effect needs.
			 */
			if (this.state.overlayElement && currentTime - (this.state.lastRadiusSync ?? 0) > 1000) {
				this.state.lastRadiusSync = currentTime
				syncOverlayRadius(this.state.overlayElement, this.state.element)
			}

			switch (this.state.effect) {
				case 'facade':
					animateFacade(this.state, currentTime)
					break
				case 'samwa':
					animateSamwa(this.state, currentTime)
					break
				case 'howl':
					animateHowl(this.state, currentTime)
					break
				case 'attention':
					animateAttention(this.state, currentTime)
					break
				case 'snow':
					animateSnow(this.state, currentTime)
					break
				case 'starfield':
					animateStarfield(this.state, currentTime)
					break
				case 'gift':
					animateGift(this.state, currentTime)
					break
			}

			this.state.animationId = requestAnimationFrame(animate)
		}

		this.state.animationId = requestAnimationFrame(animate)
	}

	private cleanup(): void {
		if (!this.state) return

		if (this.state.animationId) {
			cancelAnimationFrame(this.state.animationId)
		}

		if (this.state.observer) {
			this.state.observer.disconnect()
		}

		if (this.state.samwa) {
			this.state.samwa.leafPool.clear()
		}

		if (this.state.howl) {
			this.state.howl.steamPool.clear()
		}

		if (this.state.attention) {
			this.state.attention.boltPool.clear()
		}

		if (this.state.snow) {
			this.state.snow.snowflakePool.clear()
		}

		if (this.state.overlayElement) {
			this.state.overlayElement.remove()
		}

		if (this.state.originalPosition !== undefined) {
			this.state.element.style.position = this.state.originalPosition
		}

		/** gift writes `isolation` on the host to contain its below-content layer. */
		if (this.state.originalIsolation !== undefined) {
			this.state.element.style.isolation = this.state.originalIsolation
		}

		this.state = null
	}

	override disconnected(): void {
		this.cleanup()
	}
}

export const art = directive(ArtDirective)
